import { Benefit, RenderingDetails, RenderingDetailsMeta, TGCTAs } from "@swiggy-private/cart-types";
import { isEmpty } from "@swiggy-private/js-utils";
import {
    checkAndUpdateLifetimeSavings,
    CTATypes,
    isNudgeTypeOneTap,
    isSuperV3Benefits,
    isSuperV3Nudge,
    STORAGE_KEYS,
    TooltipContext,
} from "@swiggy-private/super-ui";

interface IParams {
    isAddPlan?: boolean;
    title?: string;
    ctaText?: string;
    planId?: string;
    planName?: string;
    subtitle?: TGCTAs;
    planPrice?: string;
    benefits?: Benefit;
    totalSavings?: string;
    onImpressionSuperOneTapAddNudge?: (data: {
        title: string;
        ctaText: string;
        planId: string;
        planName: string;
        planPrice: string;
        subtitle?: string;
    }) => void;
    onImpressionSuperViewAllBenefitsCTA?: (ctaText: string, ctaLink: string, toolTipContext?: TooltipContext) => void;
    onImpressionSwiggyOneSavingsNudge?: (benefits: Benefit, totalSavings?: string) => void;
}

export const superOnLoad = ({
    isAddPlan,
    title,
    ctaText,
    planId,
    planName,
    planPrice,
    subtitle,
    benefits,
    totalSavings,
    onImpressionSuperOneTapAddNudge,
    onImpressionSuperViewAllBenefitsCTA,
    onImpressionSwiggyOneSavingsNudge,
}: IParams) => {
    if (isAddPlan) {
        onImpressionSuperOneTapAddNudge?.({
            title: title || "-",
            ctaText: ctaText || "-",
            planId: planId || "-",
            planName: planName || "-",
            planPrice: planPrice || "-",
            subtitle: subtitle?.text || "-",
        });
    }

    const isBenefitsDeeplinkCTAType = subtitle?.type === CTATypes.BENEFITS_LIST_DEEPLINK;

    if (isBenefitsDeeplinkCTAType) {
        onImpressionSuperViewAllBenefitsCTA?.(subtitle?.text || "-", subtitle?.link || "-");
    }

    if (!isEmpty(benefits)) {
        onImpressionSwiggyOneSavingsNudge?.(benefits, totalSavings || "");
    }
};

export const getNudgeAndBenefitsItems = (subscriptionSavingsRenderingDetails: RenderingDetails[]) => {
    const nudgeItems = subscriptionSavingsRenderingDetails.filter(item => isSuperV3Nudge(item?.type));
    const benefitsItems = subscriptionSavingsRenderingDetails.filter(item => isSuperV3Benefits(item?.type));

    return { nudgeItems, benefitsItems };
};

export const getSubtitleAndButtonData = (meta: RenderingDetailsMeta) => {
    const subtitle = meta?.ctas?.find((item: TGCTAs) => item?.type === CTATypes.BENEFITS_LIST_DEEPLINK);
    const oneTapButton = meta?.ctas?.find(
        (item: TGCTAs) => item?.type === CTATypes.ONE_TAP_ADD || item?.type === CTATypes.ONE_TAP_REMOVE,
    );

    return { subtitle, oneTapButton };
};

export const getSuperWidgetData = (subscriptionSavingsRenderingDetails: RenderingDetails[]) => {
    const { nudgeItems, benefitsItems } = getNudgeAndBenefitsItems(subscriptionSavingsRenderingDetails);
    const isNudgeItemPresent = nudgeItems?.length > 0;
    const isBenefitsItemPresent = benefitsItems?.length > 0;
    const isOneTapNudge =
        (isNudgeItemPresent || isBenefitsItemPresent) &&
        isNudgeTypeOneTap(nudgeItems?.[0]?.meta || benefitsItems?.[0]?.meta || {});

    // SUPER_V3_NUDGE is present
    const isNudgeUi = isNudgeItemPresent && isOneTapNudge;
    // SUPER_V3_BENEFITS has ctas i.e. we are showing user to remove the swiggy one plan after addition
    const isBenefitsOneTapUi = isBenefitsItemPresent && benefitsItems[0]?.meta?.ctas?.length > 0 && isOneTapNudge;
    const isActiveUserUi = isBenefitsItemPresent && !isOneTapNudge;

    const widgetData = isNudgeUi ? nudgeItems?.[0] : benefitsItems?.[0];

    return { isNudgeUi, isBenefitsOneTapUi, isActiveUserUi, widgetData };
};

export const pickSubtitles = (secondarySubtitles: string[], identifiers: { milestone: string }) => {
    if (isEmpty(secondarySubtitles)) {
        return "";
    }

    const shouldShowSecondarySubtitles = checkAndUpdateLifetimeSavings(
        STORAGE_KEYS.LIFETIME_SAVINGS_FREQUENCY_EXPIRED,
        identifiers?.milestone,
    );

    return shouldShowSecondarySubtitles ? secondarySubtitles?.[0] : "";
};
